import React from "react";
import { Phone, Mail } from "lucide-react";

/**
 * Thin utility header shown above the main navbar.
 * ~40px tall on desktop, wraps gracefully on mobile.
 */
export default function UtilityBar({ settings }) {
  const s = settings || {};
  const primaryCall = s.phone || "+91 81067 19123";
  const whatsapp = s.whatsapp || "+91 83093 52443";
  const email = s.email || "sales@ssk21.net";
  const waNumber = whatsapp.replace(/[^0-9]/g, "");
  const emailHref = `mailto:${email}?subject=${encodeURIComponent("Product Enquiry – SSK Enterprises")}`;

  return (
    <div
      className="bg-[#B5121B] text-white text-[12px] font-medium"
      data-testid="utility-bar"
      role="complementary"
      aria-label="Contact strip"
    >
      <div className="container-x h-9 md:h-10 flex flex-wrap items-center justify-between gap-x-5 gap-y-1">
        {/* Left — contact quick-actions */}
        <div className="flex items-center gap-4 md:gap-5 overflow-hidden">
          <a
            href={`tel:${primaryCall.replace(/\s/g, "")}`}
            className="inline-flex items-center gap-1.5 hover:text-white/85 whitespace-nowrap"
            data-testid="utility-call"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2.4} aria-hidden="true" />
            <span className="hidden sm:inline">Calls:</span> {primaryCall}
          </a>

          <a
            href={`https://wa.me/${waNumber}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-white/85 whitespace-nowrap"
            data-testid="utility-whatsapp"
            aria-label={`Chat on WhatsApp ${whatsapp}`}
            title={`WhatsApp ${whatsapp}`}
          >
            <WhatsAppIcon className="w-3.5 h-3.5" />
            <span className="hidden md:inline">WhatsApp</span>
          </a>

          <a
            href={emailHref}
            className="inline-flex items-center gap-1.5 hover:text-white/85 whitespace-nowrap"
            data-testid="utility-email"
          >
            <Mail className="w-3.5 h-3.5" strokeWidth={2.4} aria-hidden="true" />
            <span className="hidden sm:inline">Email:</span> {email}
          </a>
        </div>

        {/* Right — business hours + social icons */}
        <div className="hidden md:flex items-center gap-4 lg:gap-5">
          <div className="inline-flex items-center gap-3 text-[11px] text-white/95">
            <span className="whitespace-nowrap"><strong className="font-semibold">Mon–Sat:</strong> 9:00 AM – 7:00 PM</span>
            <span className="w-px h-3 bg-white/40" aria-hidden="true" />
            <span className="whitespace-nowrap"><strong className="font-semibold">Sunday:</strong> Closed</span>
          </div>
          <div className="inline-flex items-center gap-2 pl-3 border-l border-white/25">
            <a
              href={s.linkedin || "https://linkedin.com/"}
              target="_blank"
              rel="noreferrer"
              className="hover:text-white/85"
              data-testid="utility-linkedin"
              aria-label="LinkedIn"
              title="LinkedIn"
            >
              <LinkedInIcon className="w-3.5 h-3.5" />
            </a>
            <a
              href={`https://wa.me/${waNumber}`}
              target="_blank"
              rel="noreferrer"
              className="hover:text-white/85"
              data-testid="utility-social-wa"
              aria-label="WhatsApp"
              title="WhatsApp"
            >
              <WhatsAppIcon className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Inline WhatsApp glyph (Lucide has no dedicated WhatsApp icon)
function WhatsAppIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.86 2.708.86.688 0 2.005-.28 2.223-1.005.13-.42.13-.795.09-.9-.075-.175-.29-.265-.615-.42zm-3.213 3.928c-1.68 0-3.286-.518-4.688-1.404l-3.31 1.05 1.076-3.19a8.674 8.674 0 0 1-1.5-4.878c0-4.816 3.913-8.72 8.72-8.72 4.807 0 8.72 3.913 8.72 8.72 0 4.808-3.913 8.722-8.72 8.722zm7.03-15.75A9.858 9.858 0 0 0 15.895 2.5c-5.435 0-9.865 4.43-9.865 9.86 0 1.736.457 3.435 1.326 4.918L5.5 22.375l5.207-1.5c1.42.775 3.018 1.19 4.72 1.19h.005c5.436 0 9.867-4.428 9.867-9.86 0-2.638-1.025-5.115-2.882-6.972z" />
    </svg>
  );
}

function LinkedInIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.852-3.037-1.853 0-2.136 1.446-2.136 2.94v5.666H9.352V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zM7.119 20.452H3.552V9H7.12v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}
